import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ColumnConfigItem, CustomColumnDefinition } from '../../../types/lead';
import { CUSTOM_COLUMN_TYPES } from './types';
import { SortableItemEditingView } from './SortableItemEditingView';
import { SortableItemView } from './SortableItemView';

export interface SortableItemProps {
  column: ColumnConfigItem;
  customColumns?: CustomColumnDefinition[];
  onToggleVisibility: (key: string) => void;
  onRename?: (key: string) => void;
  onDeactivate?: (key: string) => void;
  isEditing?: boolean;
  editingName?: string;
  renameError?: string | null;
  onEditingNameChange?: (val: string) => void;
  onSaveRename?: () => void;
  onCancelRename?: () => void;
  isHighlighted?: boolean;
}

export const SortableItem = React.memo(function SortableItem({
  column,
  customColumns,
  onToggleVisibility,
  onRename,
  onDeactivate,
  isEditing,
  editingName,
  renameError,
  onEditingNameChange,
  onSaveRename,
  onCancelRename,
  isHighlighted,
}: SortableItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.key,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    position: 'relative' as const,
    opacity: 1,
  };

  const colType = column.type || customColumns?.find((c) => c.key === column.key)?.type;
  const dataTypeLabel = colType
    ? CUSTOM_COLUMN_TYPES.find((t) => t.type === colType)?.label
    : undefined;

  if (isEditing) {
    return (
      <SortableItemEditingView
        setNodeRef={setNodeRef}
        columnKey={column.key}
        style={style}
        attributes={attributes}
        listeners={listeners}
        editingName={editingName}
        onEditingNameChange={onEditingNameChange}
        onSaveRename={onSaveRename}
        onCancelRename={onCancelRename}
        renameError={renameError}
      />
    );
  }

  return (
    <SortableItemView
      setNodeRef={setNodeRef}
      column={column}
      style={style}
      attributes={attributes}
      listeners={listeners}
      dataTypeLabel={dataTypeLabel}
      isDragging={isDragging}
      isHighlighted={isHighlighted}
      onToggleVisibility={onToggleVisibility}
      onRename={onRename}
      onDeactivate={onDeactivate}
    />
  );
});
